import { DomOverlay } from './DomOverlay';
import { Haptics } from '../Haptics';
import { TIER_NAMES, Tier } from '../../config';

type GameOverEvents = {
  newRun: void;
  menu: void;
};

export interface GameOverStats {
  score: number;
  highScore: number;
  runMerges: number;
  longestChain: number;
  highestTier: Tier;
  isNewRecord: boolean;
}

/**
 * GameOverOverlay — DOM-over-canvas end-of-run summary.
 *
 * Premium glass panel, spring-in stats animation, primary "New Run" CTA plus
 * a secondary "Main Menu" exit. Haptic error on mount (designer brief §5).
 */
export class GameOverOverlay extends DomOverlay<GameOverEvents> {
  private stats: GameOverStats;

  constructor(stats: GameOverStats) {
    super();
    this.stats = stats;
  }

  protected render(): void {
    this.rootEl.innerHTML = '';
    this.rootEl.appendChild(this.buildContent());

    if (!document.getElementById('gameover-overlay-styles')) {
      const style = this.el('style');
      style.id = 'gameover-overlay-styles';
      style.textContent = GAMEOVER_STYLES;
      document.head.appendChild(style);
    }

    // Fire an error haptic once the overlay enters — confirms the run ended.
    Haptics.error();
  }

  private buildContent(): HTMLDivElement {
    const wrap = this.el('div', 'gameover-wrap');
    const panel = this.el('div', 'gameover-panel glass-panel glass-panel--strong spring-in');

    const eyebrow = this.el('div', 'type-heading gameover-eyebrow', 'RUN COMPLETE');
    const title = this.el('h1', 'type-display gameover-title', 'Game Over');
    panel.appendChild(eyebrow);
    panel.appendChild(title);

    if (this.stats.isNewRecord) {
      const record = this.el('div', 'gameover-record', 'NEW HIGH SCORE');
      panel.appendChild(record);
    }

    // Primary score readout — big JetBrains Mono
    const scoreBlock = this.el('div', 'gameover-score');
    const scoreLabel = this.el('div', 'type-micro', 'FINAL SCORE');
    const scoreValue = this.el('div', 'gameover-score__value type-numeric', this.stats.score.toLocaleString());
    scoreBlock.appendChild(scoreLabel);
    scoreBlock.appendChild(scoreValue);
    panel.appendChild(scoreBlock);

    // Secondary stats grid
    const grid = this.el('div', 'gameover-grid');
    const rows: Array<[string, string]> = [
      ['Previous Best', this.stats.highScore.toLocaleString()],
      ['Total Merges',  this.stats.runMerges.toString()],
      ['Longest Chain', `${this.stats.longestChain}x`],
      ['Highest Tier',  TIER_NAMES[this.stats.highestTier] ?? '—'],
    ];
    for (const [label, value] of rows) {
      const row = this.el('div', 'gameover-grid__row');
      row.appendChild(this.el('span', 'gameover-grid__label type-body', label));
      row.appendChild(this.el('span', 'gameover-grid__value type-numeric', value));
      grid.appendChild(row);
    }
    panel.appendChild(grid);

    // Buttons
    const actions = this.el('div', 'gameover-actions');
    const newRun = this.el('button', 'btn btn--filled gameover-btn', 'New Run');
    newRun.addEventListener('click', () => {
      Haptics.heavy();
      this.emit('newRun', undefined as never);
    });
    const menu = this.el('button', 'btn btn--ghost gameover-btn', 'Main Menu');
    menu.addEventListener('click', () => {
      Haptics.selection();
      this.emit('menu', undefined as never);
    });
    actions.appendChild(newRun);
    actions.appendChild(menu);
    panel.appendChild(actions);

    wrap.appendChild(panel);
    return wrap;
  }
}

const GAMEOVER_STYLES = `
.gameover-wrap {
  width: 100%;
  max-width: 380px;
  display: flex;
  justify-content: center;
}
.gameover-panel {
  width: 100%;
  padding: 30px 28px 26px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 14px;
}
.gameover-eyebrow {
  text-align: center;
  margin: 0;
  color: var(--accent-magenta);
  letter-spacing: 0.18em;
}
.gameover-title {
  text-align: center;
  font-size: 40px;
  margin: 0 0 4px;
  letter-spacing: 0.01em;
}
.gameover-record {
  text-align: center;
  font-family: var(--font-display);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.22em;
  color: var(--accent-gold);
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(255, 214, 0, 0.08);
  border: 1px solid rgba(255, 214, 0, 0.4);
  align-self: center;
  animation: pulse-glow 1.6s ease-in-out infinite;
  box-shadow: 0 0 24px -6px rgba(255, 214, 0, 0.7);
}
.gameover-score {
  text-align: center;
  padding: 12px 0 6px;
}
.gameover-score__value {
  font-size: 54px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--accent-cyan);
  text-shadow: 0 0 28px rgba(0, 240, 255, 0.55);
  line-height: 1;
  margin-top: 4px;
}
.gameover-grid {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 4px;
  border-top: 1px solid rgba(136, 146, 176, 0.12);
  border-bottom: 1px solid rgba(136, 146, 176, 0.12);
}
.gameover-grid__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}
.gameover-grid__label { font-size: 12px; color: var(--text-secondary); }
.gameover-grid__value { font-size: 14px; color: var(--text-primary); }
.gameover-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 8px;
}
.gameover-btn { width: 100%; }
`;
